import { Button, cn } from '@newfront-insurance/core-ui/v2';
import { useFileSelection } from '@newfront-insurance/nova-core';
import { Paperclip } from 'lucide-react';
import { useEffect, useRef } from 'react';

import { useAttachments } from './attachments-provider';
import { useDragHandlers } from './chat-input-context';
import { useChatInputContext } from './chat-input-context';
import { useChatOverlayContext } from '../../contexts/chat-overlay-context';

interface ChatAttachmentsProps {
  className?: string;
}

/**
 * Component for managing file attachments in chat input.
 * Manages its own attachment state and file selection logic.
 * Contributes attachment data to additionalContext via ChatOverlayContext.
 *
 * This component is decoupled from the input setup - it simply sets additionalContext
 * which is then used when the message is sent.
 *
 * Includes only the attachment button and file input.
 * Note: The attachments preview and web search warning are rendered separately.
 */
export function ChatAttachments({ className }: ChatAttachmentsProps) {
  // Manage attachments state locally
  const { attachments, setAttachments } = useAttachments();
  const { setAdditionalContext } = useChatOverlayContext();
  const { setHandlers } = useDragHandlers();
  const { status } = useChatInputContext();

  // Use file selection hook with local state
  const {
    fileInputRef,
    isDragging,
    handleFileUpload,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handlePaste,
    handleAttachmentClick,
    supportedFileTypes,
  } = useFileSelection({ setAttachments });

  // Track previous status to detect when message is sent
  const prevStatusRef = useRef(status);

  // Clear attachments when message is sent
  useEffect(() => {
    if (prevStatusRef.current !== 'submitted' && status === 'submitted') {
      // Message was just sent, clear attachments
      setAttachments([]);
      setAdditionalContext((prev) => {
        const { attachmentFileUuids, messageMetadata, attachments: _, ...rest } = prev;
        return rest;
      });
    }
    prevStatusRef.current = status;
  }, [status, setAttachments, setAdditionalContext]);

  // Update drag handlers ref whenever they change
  useEffect(() => {
    setHandlers({
      isDragging,
      handleDragOver,
      handleDragLeave,
      handleDrop,
      handlePaste,
    });
  }, [isDragging, handleDragOver, handleDragLeave, handleDrop, handlePaste, setHandlers]);

  // Update additionalContext with attachment data whenever it changes
  useEffect(() => {
    const successfulAttachments = attachments.filter((attachment) => attachment.fileUuid && !attachment.uploadError);
    const attachmentFileUuids = successfulAttachments.map((attachment) => attachment.fileUuid!);
    const fileMetadata = successfulAttachments.map((attachment) => ({
      uuid: attachment.fileUuid!,
      filename: attachment.file.name,
    }));

    setAdditionalContext((prev) => ({
      ...prev,
      attachmentFileUuids,
      // Set messageMetadata that will be attached to the message when sent
      // Components can contribute metadata by setting messageMetadata in additionalContext
      messageMetadata: fileMetadata.length > 0 ? { files: fileMetadata } : undefined,
    }));
  }, [attachments, setAdditionalContext]);

  return (
    <div className={cn('flex items-center', className)}>
      <Button
        data-testid="attachment-button"
        variant="transparent"
        className={cn(
          'text-muted-foreground h-fit rounded-full bg-transparent p-2 transition-colors',
          'hover:bg-rebrand-400 border-none hover:text-white active:text-white',
        )}
        onClick={handleAttachmentClick}
        type="button"
      >
        <Paperclip size={16} />
      </Button>

      <input
        ref={fileInputRef}
        type="file"
        accept={supportedFileTypes.join(',')}
        multiple
        onChange={handleFileUpload}
        style={{ display: 'none' }}
      />
    </div>
  );
}
